import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import { formatDate, getInitials } from "@/lib/utils";
import { useNoticesQuery } from "@/hooks/useNoticesQuery";
import type { Location } from "@/types/Location";

interface LocationNotificationProps {
  location: Location;
}

// these are also called practice notices
const LocationNotification = ({ location }: LocationNotificationProps) => {
  const { data: notices = [], isLoading: loading } = useNoticesQuery(location.id);

  const renderEmpty = () => {
    return (
      <Empty className="bg-gray-100 border-2 border-gray-100 rounded-lg p-6 border-solid">
        <EmptyHeader>
          <EmptyTitle className="tpr-font text-2xl">No current notices for this location.</EmptyTitle>
        </EmptyHeader>
        <EmptyDescription className="max-w-2/3 mx-auto text-center">
          Keep an eye on this space for updates from the practice manager.
        </EmptyDescription>
      </Empty>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-16">
        <Spinner className="size-6 mr-2" />
        <span className="text-sm">Loading notices...</span>
      </div>
    );
  }

  if (!notices || notices.length === 0) {
    return renderEmpty();
  }

  return (
    <div>
      {notices.map((notice) => (
        <div key={notice.id} className="flex items-start gap-2.5 mb-2">
          <Avatar className="w-8 h-8">
            <AvatarImage
              src={
                notice.author?.avatar ||
                "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
              }
              alt={`${notice.author?.firstName} ${notice.author?.lastName}` || "User Avatar"}
            />
            <AvatarFallback>
              {getInitials(`${notice.author?.firstName} ${notice.author?.lastName}`)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col w-full leading-1.5 p-4 border-gray-200 bg-amber-100 rounded-e-xl rounded-es-xl dark:bg-gray-700">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {notice.author?.firstName} {notice.author?.lastName}
              </span>
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                {formatDate(notice.createdAt ? new Date(notice.createdAt) : undefined)}
              </span>
            </div>
            <p className="text-sm font-normal py-2.5 text-gray-900 dark:text-white">{notice.message}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default LocationNotification;
